import { api } from './client'

/** How a delivery ended overall; null while still pending. */
export type DeliveryOutcome =
  | 'dropped'
  | 'unmatched'
  | 'routed'
  | 'sunk'
  | 'partial'
  | 'failed'

/** What the route's emit did about invoking agents. */
type RouteInvocation =
  | 'invoked'
  | 'coalesced'
  | 'capped'
  | 'no_subscribers'
  | 'thread_resolved'
  | 'already_emitted'
  | 'thread_closed'

export interface DeliveryRoute {
  route_id: string
  route_name: string | null
  topic_id: number
  state: 'pending' | 'committed' | 'failed'
  event_id: string | null
  thread_id: string | null
  error: string | null
  invocation: RouteInvocation | null
  /** Agents invoked (when `invocation` is `invoked`). */
  delivered: number | null
}

export interface Delivery {
  delivery_key: string
  event_type: string | null
  correlation_key: string | null
  /** The lifecycle fact normalize stated about the entity, if any. */
  entity: 'closed' | 'updated' | 'reopened' | null
  outcome: DeliveryOutcome | null
  /** For a dropped delivery: the 0-based normalize rule that dropped it. */
  drop_rule: number | null
  state: 'committed' | 'pending'
  /** Epoch ms. */
  first_seen_at: number
  routes: DeliveryRoute[]
}

/** The latest request the endpoint refused before accepting it. */
interface IngressRejection {
  reason: string
  count: number
  /** Epoch ms. */
  last_at: number
}

export interface DeliveriesResponse {
  deliveries: Delivery[]
  last_rejection: IngressRejection | null
}

/** A resource that can feed an event route (any kind that receives events). */
export interface RouteSource {
  id: string
  name: string
  kind: string
  /** Set for topic-scoped connections (e.g. webhooks). */
  topic_id: string | null
}

/** How senders reach a connection. */
export interface IngressInfo {
  /** The deployment manages how senders reach this connection. */
  managed: boolean
  /** Absolute when the instance knows its public URL, else a path. */
  endpoint_url: string
  has_secret: boolean
  signature_header: string
  signature_prefix: string
  delivery_header: string | null
  delivery_header_required: boolean
}

export interface IngressSecret {
  /** Shown once; stored sealed. */
  secret: string
  info: IngressInfo
}

export const ingressApi = {
  /** Newest-first trace of a connection's recent deliveries (up to 50) and
   * what each route did with them. */
  deliveries: (workspaceId: string, resourceId: string) =>
    api.get<DeliveriesResponse>(
      `/api/workspaces/${workspaceId}/resources/${resourceId}/deliveries`,
    ),
  info: (workspaceId: string, resourceId: string) =>
    api.get<IngressInfo>(
      `/api/workspaces/${workspaceId}/resources/${resourceId}/ingress`,
    ),
  /** Replace the signing secret with a generated one, returned once. */
  rotateSecret: (workspaceId: string, resourceId: string) =>
    api.post<IngressSecret>(
      `/api/workspaces/${workspaceId}/resources/${resourceId}/ingress-secret`,
      {},
    ),
  /** Everything in the workspace that can source a route. */
  routeSources: (workspaceId: string) =>
    api.get<RouteSource[]>(`/api/workspaces/${workspaceId}/route-sources`),
}
